import React from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentType, type Balance, type SelectedPackage } from '@/models';
import { useBookingStore, useBrandStore } from '@/store';

import Button from '../../components/Button';

interface BookingCostSummaryProps {
  onConfirm?: () => void;
  confirmLabel?: string;
  totalLabel?: string; // customizable total label e.g. "Total Remaining"
  overrideBalance?: Balance;
  overrideSelectedPackages?: SelectedPackage[];
}

/**
 * BookingCostSummary
 * Extracted common footer cost breakdown used by PaymentSummary & PackageSelection pages.
 * Uses balance data from the booking store instead of props.
 */
const BookingCostSummary: React.FC<BookingCostSummaryProps> = ({
  onConfirm,
  confirmLabel,
  totalLabel,
  overrideBalance,
  overrideSelectedPackages,
}) => {
  const { t } = useTranslation();
  const { balance: storeBalance, selectedPackages: storeSelectedPackages } =
    useBookingStore();
  const { getPaymentType } = useBrandStore();

  const balance = overrideBalance ?? storeBalance;
  const selectedPackages = overrideSelectedPackages ?? storeSelectedPackages;

  const {
    subtotal,
    promotions = [],
    promotionDiscount = 0,
    packageDiscount = 0,
    total,
    depositTotal,
    remainingTotal,
  } = balance;

  const paymentType = getPaymentType();

  const confirmLabelToShow =
    confirmLabel ??
    t(
      paymentType === PaymentType.None || total === 0
        ? 'bookingCostSummary.confirmBooking'
        : 'bookingCostSummary.continueToPayment',
    );
  const totalLabelToShow =
    totalLabel ??
    t(
      paymentType === PaymentType.Deposit &&
        balance.depositTotal < balance.total
        ? 'bookingCostSummary.depositToPay'
        : 'bookingCostSummary.total',
    );

  return (
    <div className="flex w-full flex-col items-start gap-2">
      {/* Subtotal */}
      <div className="flex w-full items-center justify-between gap-2 text-black">
        <div className="flex-1">
          <p>{t('bookingCostSummary.subtotal')}</p>
        </div>
        <div className="whitespace-nowrap">
          <p>
            {t('common.currency')}
            {subtotal.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Promotions */}
      {promotions.length > 0 && (
        <div className="flex w-full flex-col gap-1">
          {promotions.map((p, idx) => (
            <div
              key={idx}
              className="flex w-full items-start justify-between gap-2"
            >
              <div className="text-primary-text">
                <p>
                  {t('bookingCostSummary.promotion')}: {p.name}
                </p>
              </div>
              <div className="text-error whitespace-nowrap">
                <p>
                  - {t('common.currency')}
                  {p.discountAmount.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
          {promotionDiscount > 0 && promotions.length > 1 && (
            <div className="text-primary-text flex w-full justify-between text-sm">
              <span>{t('bookingCostSummary.totalPromotionDiscount')}</span>
              <span className="text-error">
                - {t('common.currency')}
                {promotionDiscount.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Packages */}
      {selectedPackages.length > 0 && (
        <div className="flex w-full items-start justify-between gap-2">
          <div className="text-secondary-text">
            <p>
              {t('bookingCostSummary.package')}
              {` ${t('bookingCostSummary.appliedCount', { count: selectedPackages.length })}`}
            </p>
          </div>
          {packageDiscount > 0 ? (
            <div className="text-error whitespace-nowrap">
              - {t('common.currency')}
              {packageDiscount.toLocaleString()}
            </div>
          ) : (
            <div className="text-error">0</div>
          )}
        </div>
      )}

      {/* Remaining */}
      {paymentType !== PaymentType.Full && remainingTotal > 0 && (
        <div className="flex w-full items-center justify-between">
          <div className="text-secondary-text">
            {t('thankYou.paymentInformation.remainingAtStore')}
          </div>
          <div className="text-secondary-text whitespace-nowrap">
            {t('common.currency')}
            {remainingTotal.toLocaleString()}
          </div>
        </div>
      )}

      {/* Total */}
      <div className="flex w-full items-center justify-between text-xl font-semibold">
        <div className="text-black">{totalLabelToShow}</div>
        <div className="text-primary-text whitespace-nowrap">
          {t('common.currency')}
          {(paymentType === PaymentType.Deposit
            ? depositTotal
            : total
          ).toLocaleString()}
        </div>
      </div>

      {onConfirm && (
        <Button type="button" onClick={onConfirm}>
          {confirmLabelToShow}
        </Button>
      )}
    </div>
  );
};

export default BookingCostSummary;
